import { artConfigBuildings } from "../assetBundles/artConfigBuildings";
import { artConfigProductions } from "../assetBundles/artConfigProductions";
import { IdBuilding } from "../constants/IdBuilding";
import { IdAssetBundleCore } from "../features/loading/IdAssetBundleCore";
import { utilAnimation } from "../utils/animation/utilAnimation";
import { logGraphics, warn } from "../utils/logging/logging";
import { utilBuildings } from "../utils/utilBuildings";
import { utilEnvironment } from "../utils/utilEnvironment";
import { BF2BoostedFieldAnimation } from "./BF2BoostedFieldAnimation";
import { BF2AddProductionAnim, BF2CreateAsset, BF2GetConfigBasedBuilding, } from "./BF2BuildingGraphicsUtils";
import { BF2ChimneySmokeParticleAnim } from "./BF2ChimneySmokeParticleAnim";
import { BF2ProductionAnimator } from "./BF2ProductionAnimator";
import { GetArtAssetId } from "./GetArtAssetId";
import { GetBuildingScaffoldingIso } from "./GetBuildingScaffoldingIso";
import { LoadingBuildingGraphicIso } from "./LoadingBuildingGraphicIso";
import { WalkingCitizensAnimator } from "./WalkingCitizensAnimator";
var animatorsByBuildingId = new Map();
var loadingGraphicsByBuildingId = new Map();
var walkingCitizensByBuildingId = new Map();
var smokeAnimationByBuildingId = new Map();
var boostedFieldAnimationByBuildingid = new Map();
/**
 * Has to provide hitArea for whatever it returns. Otherwise clicks won't work on buildings.
 * @param {IBuildingData} data
 * @returns {PIXI.DisplayObject}
 */
export var GetBuildingGraphicIso = function (data) {
    var id = data.proto.id || -1;
    try {
        var balancing = data.balancing, production = data.production;
        var level = utilBuildings.getVisualLevel(data);
        var buildingName = GetArtAssetId(balancing.name, level);
        var config = artConfigBuildings[buildingName];
        loadingGraphicsByBuildingId.delete(id);
        var container_1 = BF2GetConfigBasedBuilding(data, config);
        if (balancing.id === IdBuilding.SimpleHouse) {
            addSmokeAnimation(id, container_1, buildingName);
        }
        // Add production graphics
        if (production != undefined) {
            var isWindmillBuilding = balancing.id === IdBuilding.Windmill;
            var productionKey = isWindmillBuilding && utilAnimation.isAnimationEnabled()
                ? "" + balancing.name + level
                : "" + production.recipe.visualName;
            var prodConfig_1 = artConfigProductions[productionKey];
            if (prodConfig_1 == undefined) {
                if (utilEnvironment.isDevelopment()) {
                    warn("Production config not found for building: " + balancing.name + " level: " + level);
                }
                return container_1;
            }
            var step = utilBuildings.getProductionCurrentStep(data, prodConfig_1.length);
            var hasAnimation = productionHasAnimation(prodConfig_1);
            if (hasAnimation) {
                var animator_1 = animatorsByBuildingId.get(id);
                if (animator_1 == undefined || utilAnimation.isAnimationEnabled()) {
                    animator_1 = new BF2ProductionAnimator(id, balancing.id);
                    animatorsByBuildingId.set(id, animator_1);
                    prodConfig_1[0].forEach(function (animData) {
                        BF2AddProductionAnim(animData, animator_1, prodConfig_1[0].length > 1);
                    });
                }
                var prodStep = utilBuildings.getProductionCurrentStep(data, prodConfig_1[0][0].anims.length);
                // step 0 will never be used directly, so if it's 0 we should take 1 which is production state
                var prodAnimStep = Math.max(prodStep, 1);
                animator_1.setContainer(container_1);
                animator_1.setCurrentProductionStep(prodAnimStep);
            }
            else if (step !== -1) {
                prodConfig_1[step].forEach(function (asset) {
                    var sprite = BF2CreateAsset(asset);
                    container_1.addChild(sprite);
                });
            }
            addWalkingCitizensAnimation(id, container_1);
            if (production.production != undefined &&
                production.production.boosterId != undefined) {
                addFertilizerAnimation(id, container_1);
            }
            else {
                clearFertilizerAnimation(id);
            }
        }
        return container_1;
    }
    catch (err) {
        logGraphics("Unable to get building graphics:", data, err);
        if (loadingGraphicsByBuildingId.has(id)) {
            return loadingGraphicsByBuildingId.get(id);
        }
        var loadingGraphic = new LoadingBuildingGraphicIso({
            building: data,
            bundleId: IdAssetBundleCore.Buildings,
            getGraphicsIso: GetBuildingGraphicIso,
            getLoadingIso: GetBuildingScaffoldingIso,
        });
        loadingGraphicsByBuildingId.set(id, loadingGraphic);
        return loadingGraphic;
    }
};
var addWalkingCitizensAnimation = function (id, container) {
    clearWalkingCitizens(id);
    if (utilAnimation.isAnimationEnabled()) {
        var walkingCitizensAnimator = new WalkingCitizensAnimator();
        walkingCitizensAnimator.setAnimationLoop(container, id);
        walkingCitizensByBuildingId.set(id, walkingCitizensAnimator);
    }
};
var clearWalkingCitizens = function (id) {
    if (walkingCitizensByBuildingId.has(id)) {
        var walkingCitizensAnimationInstance = walkingCitizensByBuildingId.get(id);
        walkingCitizensAnimationInstance.removeAnimation(false);
        walkingCitizensByBuildingId.delete(id);
    }
};
var addSmokeAnimation = function (id, container, buildingName) {
    clearSmokeAnimation(id);
    if (utilAnimation.isAnimationEnabled()) {
        var chimneySmokeParticleAnim = new BF2ChimneySmokeParticleAnim();
        chimneySmokeParticleAnim.startAnimation(container, buildingName);
        smokeAnimationByBuildingId.set(id, chimneySmokeParticleAnim);
    }
};
var clearSmokeAnimation = function (id) {
    if (smokeAnimationByBuildingId.has(id)) {
        var chimneySmokeParticleAnimInstance = smokeAnimationByBuildingId.get(id);
        if (chimneySmokeParticleAnimInstance) {
            chimneySmokeParticleAnimInstance.removeInterval();
        }
        smokeAnimationByBuildingId.delete(id);
    }
};
var addFertilizerAnimation = function (id, container) {
    clearFertilizerAnimation(id);
    var boostedFieldParticleAnim = new BF2BoostedFieldAnimation();
    boostedFieldAnimationByBuildingid.set(id, boostedFieldParticleAnim);
    boostedFieldParticleAnim.startAnimation(container, true);
};
var clearFertilizerAnimation = function (id) {
    if (boostedFieldAnimationByBuildingid.has(id)) {
        var emitter = boostedFieldAnimationByBuildingid.get(id);
        emitter.removeAnimation();
        boostedFieldAnimationByBuildingid.delete(id);
    }
};
var productionHasAnimation = function (config) {
    return config.length === 1 && config[0].length > 0 && config[0][0].anims != undefined;
};
export var toggleGameAnimation = function (playAnim, callbackFn) {
    if (!playAnim) {
        animatorsByBuildingId.clear();
        walkingCitizensByBuildingId.forEach(function (value) {
            return value.removeAnimation(false);
        });
        smokeAnimationByBuildingId.forEach(function (value) {
            return value.removeInterval();
        });
    }
    callbackFn();
};



//////////////////
// WEBPACK FOOTER
// ./src/graphics/GetBuildingGraphicIso.ts
// module id = 579
// module chunks = 3